import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/apiClient';
import type { Checkin } from '../lib/types';
import { useAuth } from './AuthProvider';
import { EndCheckinSheet } from '../components/checkin/EndCheckinSheet';

/**
 * Plan §20.4 — check-in flow (single source of truth).
 *
 * One active check-in per user at a time. Polls /v1/me/checkins
 * every 30 s while signed in so a check-in started on iPhone
 * shows up on the web automatically (and vice-versa).
 */
interface CheckinContextValue {
    active: Checkin | null;
    loading: boolean;
    start: (input: StartInput) => Promise<Checkin>;
    end: (id: string) => Promise<void>;
    sos: (id: string) => Promise<void>;
    refresh: () => void;
}

interface StartInput {
    lat: number;
    lon: number;
    durationMin: number;
    spotId?: string;
    spotName?: string;
}

interface CheckinsResponse { checkins: Checkin[] }

const CheckinContext = createContext<CheckinContextValue>({
    active: null,
    loading: false,
    start: async () => { throw new Error('CheckinProvider not mounted'); },
    end: async () => undefined,
    sos: async () => undefined,
    refresh: () => undefined,
});

export function CheckinProvider({ children }: { children: ReactNode }) {
    const { isSignedIn } = useAuth();
    const qc = useQueryClient();

    const q = useQuery({
        queryKey: ['me', 'checkins', 'active'],
        queryFn: () => api.get<CheckinsResponse>('/v1/me/checkins?status=active'),
        enabled: isSignedIn,
        refetchInterval: isSignedIn ? 30_000 : false,
        staleTime: 10_000,
    });

    const active = q.data?.checkins.find((c) => c.status === 'active') ?? null;

    const start = useCallback(async (input: StartInput) => {
        const id = crypto.randomUUID();
        const created = await api.post<Checkin>('/v1/me/checkins', { id, ...input });
        qc.invalidateQueries({ queryKey: ['me', 'checkins'] });
        return created;
    }, [qc]);

    const end = useCallback(async (id: string) => {
        await api.post('/v1/me/checkins/' + id + '/end', {});
        qc.invalidateQueries({ queryKey: ['me', 'checkins'] });
    }, [qc]);

    const sos = useCallback(async (id: string) => {
        await api.post('/v1/me/checkins/' + id + '/sos', {});
        qc.invalidateQueries({ queryKey: ['me', 'checkins'] });
    }, [qc]);

    return (
        <CheckinContext.Provider
            value={{
                active,
                loading: q.isLoading,
                start,
                end,
                sos,
                refresh: () => q.refetch(),
            }}
        >
            {children}
            {/* Always-on countdown banner if a check-in is active */}
            {active && <CheckinBanner checkin={active} />}
        </CheckinContext.Provider>
    );
}

export function useCheckin() {
    return useContext(CheckinContext);
}

/**
 * The "I'm in the water right now" persistent banner. Shows the
 * countdown + I'm out + Need help across every page.
 */
function CheckinBanner({ checkin }: { checkin: Checkin }) {
    const { sos } = useCheckin();
    const [now, setNow] = useState(() => Date.now());
    const [showSOS, setShowSOS] = useState(false);
    const [showEnd, setShowEnd] = useState(false);

    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);

    const expiresAt = new Date(checkin.expectedEndAt).getTime();
    const remainingMs = expiresAt - now;
    const overdue = remainingMs < 0;
    const minutes = Math.floor(Math.abs(remainingMs) / 60_000);
    const seconds = Math.floor((Math.abs(remainingMs) % 60_000) / 1000);

    return (
        <>
            <div
                className={`fixed bottom-0 left-0 right-0 z-40 border-t shadow-lg ${
                    overdue
                        ? 'bg-verdict-avoid text-white border-verdict-avoid/70'
                        : 'bg-hattie-teal text-white border-hattie-teal/70'
                }`}
            >
                <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                        <div className="text-[11px] uppercase tracking-wider font-bold opacity-80">
                            {overdue ? 'Check-in overdue' : "You're checked in"}
                        </div>
                        <div className="font-display text-xl">
                            {overdue ? '+' : ''}{String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
                            <span className="text-sm opacity-80 ml-2">
                                {overdue ? 'past your expected exit' : 'left in the water'}
                            </span>
                        </div>
                    </div>
                    <button
                        onClick={() => setShowEnd(true)}
                        className="rounded-full bg-white text-hattie-ink font-semibold px-5 py-2 text-sm hover:bg-hattie-paper"
                    >
                        I'm out
                    </button>
                    <button
                        onClick={() => setShowSOS(true)}
                        className="rounded-full bg-white/20 text-white font-semibold px-4 py-2 text-sm hover:bg-white/30 border border-white/40"
                    >
                        Need help
                    </button>
                </div>
            </div>

            {showSOS && (
                <SOSSheet
                    checkin={checkin}
                    onClose={() => setShowSOS(false)}
                    onSOSConfirmed={async () => {
                        await sos(checkin.id);
                        setShowSOS(false);
                    }}
                />
            )}

            <EndCheckinSheet
                open={showEnd}
                checkin={checkin}
                onCancel={() => setShowEnd(false)}
                onDone={() => setShowEnd(false)}
            />
        </>
    );
}

function SOSSheet({
    checkin,
    onClose,
    onSOSConfirmed,
}: {
    checkin: Checkin;
    onClose: () => void;
    onSOSConfirmed: () => void;
}) {
    const mapsLink = `https://www.google.com/maps?q=${checkin.lat},${checkin.lon}`;
    const shareText = `Coastguard / I need help — last seen at ${checkin.lat.toFixed(5)}, ${checkin.lon.toFixed(5)}. ${mapsLink}`;

    async function share() {
        if (navigator.share) {
            try {
                await navigator.share({ title: 'Wild swim — need help', text: shareText });
            } catch { /* dismissed */ }
        } else {
            try { await navigator.clipboard.writeText(shareText); window.alert('Location copied to clipboard.'); }
            catch { window.alert(shareText); }
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl">
                <div className="bg-verdict-avoid text-white px-6 pt-6 pb-4">
                    <h2 className="font-display text-3xl tracking-tight">Need help?</h2>
                    <p className="text-sm opacity-90 mt-1">
                        These shortcuts open your phone's call + share sheets — Hattie
                        does not contact emergency services on your behalf.
                    </p>
                </div>
                <div className="p-6 space-y-3">
                    <a
                        href="tel:999"
                        className="block w-full text-center rounded-2xl bg-verdict-avoid text-white py-4 font-display text-xl hover:bg-verdict-avoid/90"
                    >
                        Call 999 — Coastguard
                    </a>
                    <button
                        onClick={share}
                        className="block w-full text-center rounded-2xl bg-hattie-ink text-white py-3.5 font-semibold hover:bg-hattie-ink/90"
                    >
                        Share my location
                    </button>
                    <button
                        onClick={onSOSConfirmed}
                        className="block w-full text-center rounded-2xl bg-white border border-hattie-stone text-hattie-ink py-3.5 font-semibold hover:bg-hattie-paper"
                    >
                        Mark check-in as SOS
                    </button>
                    <button
                        onClick={onClose}
                        className="block w-full text-center rounded-2xl text-hattie-mist py-2 hover:text-hattie-ink"
                    >
                        I'm OK — close this
                    </button>
                </div>
            </div>
        </div>
    );
}
